import { datasetFormRequest } from "./dataset-form-signal";
import { FormItem } from "./form-builder.model";
import { LookupCache } from "../../services/lookup-cache";
import { notify } from "../notifications/NotificationProvider";
import { adminRpc } from "../../services/admin-rpc-client";
import { uuidv7 } from "uuidv7";
import { CheckerValue, checkpointCheckerWidget } from "./widgets/checkpoint-checker-widget";
// Import for the registration side-effect: ensures the "checkpoint-checker"
// widget is registered before this form references it.
import "./widgets/checkpoint-checker-widget";
import { errorMessage } from "../../utils/error-message";

// Shared Monitor (healthchecks) form, used by BOTH the Services view (keyed on
// the job row directly) and the Schedules view (keyed on the schedule's linked
// job). A monitor is a reusable row referenced by job.monitor_uid; this edits
// that single row so both views are one source of truth.
//
// Writes go through the SQL firewall's structured data API (dbaccess.v1
// Insert/Update via adminRpc), NOT raw SQL through the SELECT-only Query path.

const ALERT_CHANNEL_LOOKUP_KEY = "continuum:alertchannels";

export interface MonitorFormTarget {
  // Display name of the unit being monitored (service/schedule name).
  unitName: string;
  // The job uid that owns (or will own) the monitor — the FK link target.
  jobUid: string;
  // The job's current monitor_uid back-link, if any (else a new monitor is created).
  monitorUid?: string;
}

// Register the alert-channel lookup the dialog's channel picker needs. Idempotent.
export function registerMonitorLookups() {
  LookupCache.register({
    key: ALERT_CHANNEL_LOOKUP_KEY,
    sql: "from alertchannel where enabled = true select uid, name",
    valueField: "uid",
    labelField: "name",
  });
}

// parseExtra is a row's extra_data as an object: jsonb comes back as an object or, through some paths, a
// string; anything else is an empty document.
function parseExtra(raw: unknown): Record<string, unknown> {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) return { ...(raw as Record<string, unknown>) };
  if (typeof raw === "string") {
    try {
      const v = JSON.parse(raw);
      return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }
  return {};
}

// Read the existing monitor row (if any) via its uid. Includes the checker_*
// columns so the dialog can show the current custom checker.
async function loadMonitor(
  monitorUid: string | undefined,
): Promise<Record<string, unknown> | undefined> {
  if (!monitorUid) return undefined;
  const resp = await adminRpc.query(
    `SELECT uid, enabled, alert_on_nonzero_exit, alert_on_stderr, channel_uids, plugin_fileset_uid, plugin_name, extra_data FROM monitor WHERE uid = '${monitorUid.replace(/'/g, "''")}'`,
  );
  return resp.rows[0];
}

export function showMonitorForm(target: MonitorFormTarget) {
  (async () => {
    registerMonitorLookups();
    await LookupCache.ensure(ALERT_CHANNEL_LOOKUP_KEY);

    const existing = await loadMonitor(target.monitorUid);

    // channel_uids is a JSON array; milestone-1 edits a single channel (first element).
    let existingChannel = "";
    const rawChannels = existing?.["channel_uids"];
    if (Array.isArray(rawChannels)) existingChannel = (rawChannels[0] as string) ?? "";
    else if (typeof rawChannels === "string") {
      try {
        const arr = JSON.parse(rawChannels);
        if (Array.isArray(arr)) existingChannel = (arr[0] as string) ?? "";
      } catch {
        /* leave blank */
      }
    }

    const initialChecker: CheckerValue = {
      plugin_fileset_uid: (existing?.["plugin_fileset_uid"] as string) ?? null,
      plugin: (existing?.["plugin_name"] as string) ?? null,
    };

    const formItems: FormItem[] = [
      {
        name: "enabled",
        type: "toggle",
        label: "Monitoring enabled",
        helpText: "Turn off to stop alerting without losing this configuration.",
      },
      {
        name: "alert_on_nonzero_exit",
        type: "checkbox",
        label: "Fail on: non-zero exit code",
      },
      {
        name: "alert_on_stderr",
        type: "checkbox",
        label: "Fail on: stderr bytes > 0",
      },
      {
        name: "channel",
        type: "select",
        label: "Alert channel",
        options: LookupCache.getOptions(ALERT_CHANNEL_LOOKUP_KEY),
      },
      {
        name: "checker",
        type: "widget",
        widget: "checkpoint-checker",
        label: "Checker",
        // The checkpoint-checker is a COMPOSITE over the monitor's plugin columns (plugin_fileset_uid,
        // plugin_name). Render it through the new FormRenderer's render-override hook; its setValue emits
        // the whole CheckerValue object, which submitMonitorForm unpacks into the two columns.
        render: (ctx) =>
          checkpointCheckerWidget.render({
            formItem: {
              name: "checker",
              type: "widget",
              widget: "checkpoint-checker",
              label: "Checker",
            },
            formData: ctx.values,
            formErrors: {},
            setValue: ctx.onChange,
          }),
      },
    ];

    datasetFormRequest.next({
      title: `Monitor: ${target.unitName}`,
      sections: [{ title: "", items: formItems }],
      initialValues: {
        enabled: existing ? Boolean(existing["enabled"]) : true,
        alert_on_nonzero_exit: existing ? Boolean(existing["alert_on_nonzero_exit"]) : true,
        alert_on_stderr: existing ? Boolean(existing["alert_on_stderr"]) : false,
        channel: existingChannel,
        checker: initialChecker,
      },
      submitLabel: "Save monitor",
      onCancel: () => datasetFormRequest.next(undefined),
      onSubmit: async (values: Record<string, unknown>) => {
        await submitMonitorForm(target, existing?.["uid"] as string | undefined, values);
      },
    });
  })();
}

async function submitMonitorForm(
  target: MonitorFormTarget,
  existingMonitorUid: string | undefined,
  values: Record<string, unknown>,
) {
  const enabled = Boolean(values["enabled"]);
  const armExit = Boolean(values["alert_on_nonzero_exit"]);
  const armStderr = Boolean(values["alert_on_stderr"]);
  const channel = (values["channel"] as string) || "";
  const channelUids = channel ? [channel] : [];
  const channelJson = JSON.stringify(channelUids);

  const checker = (values["checker"] as CheckerValue | undefined) ?? {
    plugin_fileset_uid: null,
    plugin: null,
  };

  // channel_uids is jsonb; the structured firewall write takes it as a JSON string. A monitor is a
  // plugin and its config (FEATURE-20261025-unify-the-checker-pattern): the row names its plugin in
  // plugin_fileset_uid + plugin_name, always; no plugin picked is a run monitor, runMonitor of
  // system-healthchecks, written as such.
  let pluginFileset = checker.plugin ? checker.plugin_fileset_uid : null;
  let pluginName = checker.plugin;
  if (!pluginFileset || !pluginName) {
    const system = await adminRpc.query(`SELECT uid FROM fileset WHERE name = 'system-healthchecks' AND status = 'active' LIMIT 1`);
    pluginFileset = (system.rows[0]?.["uid"] as string | undefined) ?? null;
    pluginName = "runMonitor";
    if (!pluginFileset) {
      notify({ severity: "error", title: "Monitor not saved", message: "There is no active system-healthchecks fileset to take runMonitor from." });
      return;
    }
  }
  const monitorValues = {
    enabled,
    alert_on_nonzero_exit: armExit,
    alert_on_stderr: armStderr,
    channel_uids: channelJson,
    plugin_fileset_uid: pluginFileset,
    plugin_name: pluginName,
  };
  // dbaccess.v1.Update REPLACES extra_data: read-merge-write, keeping the keys this form does not own.
  const monitorExtra = async (): Promise<string> => {
    let extra: Record<string, unknown> = {};
    if (existingMonitorUid) {
      const rows = await adminRpc.query(
        `SELECT extra_data FROM monitor WHERE uid = '${existingMonitorUid.replace(/'/g, "''")}'`,
      );
      extra = parseExtra(rows.rows[0]?.extra_data);
    }
    delete extra["plugin"];
    const config = (extra["config"] && typeof extra["config"] === "object" ? extra["config"] : {}) as Record<string, unknown>;
    // runMonitor's config is the run's settings: the two this form edits are kept in step
    extra["config"] = pluginName === "runMonitor" ? { ...config, alertOnNonzeroExit: armExit, alertOnStderr: armStderr } : config;
    return JSON.stringify(extra);
  };

  try {
    const extra_data = await monitorExtra();
    if (existingMonitorUid) {
      // Update the existing policy in place via the firewall's structured update-data API.
      const resp = await adminRpc.update("monitor", { uid: existingMonitorUid }, { ...monitorValues, extra_data });
      if (!resp.success) throw new Error(resp.error?.message || "update monitor failed");
    } else {
      // No monitor row yet: insert one (insert-data) and link the JOB to it (update-data).
      const newMonitorUid = uuidv7().replaceAll("-", "");
      const ins = await adminRpc.insert("monitor", { uid: newMonitorUid, ...monitorValues, extra_data });
      if (!ins.success) throw new Error(ins.error?.message || "insert monitor failed");
      const link = await adminRpc.update("job", { uid: target.jobUid }, { monitor_uid: newMonitorUid });
      if (!link.success) throw new Error(link.error?.message || "link job.monitor_uid failed");
    }

    // Mirror enabled into job.extra_data.monitor_status so the existing health badge
    // reflects it. dbaccess.v1.Update REPLACES extra_data, so read-merge-write to avoid
    // clobbering other keys (service-manager metadata etc.).
    const svcRows = await adminRpc.query(
      `SELECT extra_data FROM job WHERE uid = '${target.jobUid.replace(/'/g, "''")}'`,
    );
    let extra: Record<string, unknown> = {};
    const rawExtra = svcRows.rows[0]?.extra_data;
    if (rawExtra && typeof rawExtra === "object") extra = rawExtra as Record<string, unknown>;
    else if (typeof rawExtra === "string") {
      try {
        extra = JSON.parse(rawExtra);
      } catch {
        extra = {};
      }
    }
    extra.monitor_status = enabled;
    await adminRpc.update("job", { uid: target.jobUid }, { extra_data: JSON.stringify(extra) });

    notify({
      severity: "success",
      title: enabled ? "Monitoring enabled" : "Monitoring disabled",
      message: `Monitoring for "${target.unitName}" has been ${enabled ? "enabled" : "disabled"}.`,
    });
    datasetFormRequest.next(undefined);
  } catch (err) {
    notify({
      severity: "error",
      title: "Save failed",
      message: errorMessage(err) || "Failed to save monitor configuration.",
    });
  }
}
